import React, { useState } from 'react'
import { isEmpty } from 'lodash'
import { TreeView } from '@mui/lab'
import { makeStyles, createStyles, useTheme } from '@mui/styles'

import { ObjectLiteral } from 'interfaces'
import { constructListItem } from 'components/ui-layouts/transferList/utils'
import { Box, Typography } from '@mui/material'
import { Icon, TextInput } from '@ketch-com/deck'

const useStyles = makeStyles(
  ({ typography, palette }) =>
    createStyles({
      container: {
        flex: 1,
        userSelect: 'none',
      },
      header: {
        marginBottom: 6,
      },
      scrollList: {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        border: `1px solid ${palette.iron.main}`,
        maxWidth: 518,
      },
      searchBlock: {
        display: 'flex',
        alignItems: 'center',
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        borderBottom: `1px solid ${palette.iron.main}`,
        background: palette.bone.main,
        padding: 8,
      },
      listItems: {
        height: 370,
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
        overflowY: 'auto',
      },
      listItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '4px 12px 4px 0',
        boxSizing: 'border-box',
        cursor: 'pointer',
        height: 44,
      },
      iconWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        background: palette.fadedDarkGrey.main,
        borderRadius: 4,
      },
      itemContent: {
        flex: 1,
        alignItems: 'center',
        marginLeft: 14,
      },
      icon: {
        fontSize: typography.pxToRem(12),
        color: palette.white.main,
      },
      emptyListMessage: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '40px auto',
        textAlign: 'center',
        width: 200,
      },
      treeItem: {
        '&:hover': {
          background: palette.sphereFaded.main,
        },
      },
      iconExpandWrapper: {
        height: 14,
        width: 14,
        marginLeft: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      label: {
        backgroundColor: 'unset !important',
        padding: 0,
        '&:hover': {
          background: 'none',
        },
      },
      iconContent: {
        height: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
      },
    }),
  { name: 'Tree' },
)

interface Props<T> {
  items: T[]
  title?: string
  searchString?: string
  onItemClick: (item: T) => void
  onSearch?: (nextSearchString: string) => void
  type: string
  itemRenderer: (item: T, type?: string) => React.ReactNode
  expandableKey?: string
}
export function Tree<T extends ObjectLiteral>({
  title,
  items,
  onItemClick,
  searchString,
  onSearch,
  type,
  itemRenderer,
  expandableKey,
}: Props<T>) {
  const classes = useStyles()
  const theme = useTheme()
  const [expanded, setExpanded] = useState<string[]>([])

  const handleNodeToggle = (event: React.ChangeEvent<{}>, nodes: string[]) => {
    setExpanded(nodes)
  }

  return (
    <Box
      sx={{
        flex: 1,
        userSelect: 'none',
      }}
    >
      {title && (
        <Box mb="6px">
          <Typography variant="label">{title}</Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          border: theme => `1px solid ${theme.palette.iron.main}`,
          maxWidth: 518,
        }}
      >
        {onSearch && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              borderBottom: theme => `1px solid ${theme.palette.iron.main}`,
              background: theme => theme.palette.bone.main,
              padding: 1,
            }}
          >
            <TextInput
              fullWidth
              value={searchString!}
              startIcon={<Icon name="OMag" />}
              endIcon={searchString && <Icon sx={{ cursor: 'pointer' }} name="OCross" onClick={() => onSearch('')} />}
              placeholder="Search..."
              onChange={event => onSearch(event.target.value)}
            />
          </Box>
        )}

        {isEmpty(items) && !isEmpty(searchString) && (
          <Box sx={{ height: 370, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowY: 'auto' }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              margin="40px auto"
              width={200}
            >
              <Typography variant="label" color="darkDuskFaded.main">
                No Items found matching your search criteria
              </Typography>
            </Box>
          </Box>
        )}

        {isEmpty(items) && isEmpty(searchString) && (
          <Box sx={{ height: 370, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowY: 'auto' }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              margin="40px auto"
              width={200}
            >
              <Typography variant="label" color="darkDuskFaded.main">
                No Items
              </Typography>
            </Box>
          </Box>
        )}

        {!isEmpty(items) && (
          <Box sx={{ height: 370, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowY: 'auto' }}>
            <TreeView expanded={expanded} onNodeToggle={handleNodeToggle}>
              {items.map(item =>
                constructListItem({
                  item,
                  searchString,
                  classes,
                  onItemClick,
                  type,
                  itemRenderer,
                  expandableKey,
                  expanded,
                  theme,
                }),
              )}
            </TreeView>
          </Box>
        )}
      </Box>
    </Box>
  )
}
